/* eslint-disable @typescript-eslint/no-explicit-any */
import type { BookingWithDetails } from "@/components/bookings/bookings-data-table";
import type { Profile, Room, Studio } from "@/types/database";
import { addDays, addHours, startOfDay, subDays } from "date-fns";
import type { PayoutData } from "./types";

// Demo user profile
export const DEMO_USER: Profile = {
  id: "demo-user-id",
  email: "demo@dnceclub.com",
  is_owner: true,
  full_name: "Demo Studio Owner",
  created_at: subDays(new Date(), 90).toISOString(),
};

// Demo studio
export const DEMO_STUDIO: Studio = {
  id: "demo-studio-id",
  owner_id: DEMO_USER.id,
  name: "Demo Studio",
  timezone: "America/Los_Angeles",
  location: "123 Dance Avenue, Los Angeles, CA 90028",
  bio: "Premier dance studio in the heart of LA, featuring state-of-the-art facilities and multiple practice spaces perfect for dancers of all levels.",
  photos: [
    "https://images.unsplash.com/photo-1518611012118-696072aa579a",
    "https://images.unsplash.com/photo-1535525153412-5a42439a210d",
  ],
  operating_hours: {
    monday: { open: "08:00", close: "22:00" },
    tuesday: { open: "08:00", close: "22:00" },
    wednesday: { open: "08:00", close: "22:00" },
    thursday: { open: "08:00", close: "22:00" },
    friday: { open: "08:00", close: "23:00" },
    saturday: { open: "09:00", close: "23:00" },
    sunday: { open: "10:00", close: "20:00" },
  },
  cancellation_policy: "Free cancellation up to 24 hours before rental",
  refund_policy: {
    policy_type: "tiered",
    tiers: [
      { id: "1", hours_before: 24, refund_percentage: 100 },
      { id: "2", hours_before: 12, refund_percentage: 50 },
      { id: "3", hours_before: 6, refund_percentage: 25 },
      { id: "4", hours_before: 0, refund_percentage: 0 },
    ],
  },
  customer_fee_ratio: 0.5,
  instant_book_min_hours: 2,
  stripe_connected_account_id: "acct_demo123456",
  stripe_onboarding_completed: true,
  stripe_payouts_enabled: true,
  stripe_charges_enabled: true,
  created_at: subDays(new Date(), 90).toISOString(),
};

// Demo rooms
export const DEMO_ROOMS: Room[] = [
  {
    id: "demo-room-1",
    studio_id: DEMO_STUDIO.id,
    name: "Studio A",
    description:
      "Large studio with sprung floors, mirrors, and professional sound system. Perfect for dance classes and rehearsals.",
    capacity: 30,
    equipment: {
      floor_type: "wooden_sprung",
      floor_type_custom: "",
      mirror_type: "wall_to_wall",
      speaker_connections: ["bluetooth", "3.5mm", "usb_c"],
      other_equipment: ["RGB Lighting", "Air conditioning", "Ballet barres"],
    },
    photos: ["https://images.unsplash.com/photo-1518611012118-696072aa579a"],
    is_active: true,
    dimensions: { width: 40, length: 60, unit: "ft" },
    created_at: subDays(new Date(), 90).toISOString(),
    room_rates: [
      {
        id: "rate-1",
        room_id: "demo-room-1",
        name: "Standard Rate",
        hourly_rate: 50,
        description: "Regular hourly rate",
        is_active: true,
        contact_only: false,
        created_at: subDays(new Date(),90).toISOString(),
      },
      {
        id: "rate-2",
        room_id: "demo-room-1",
        name: "Premium Rate",
        hourly_rate: 75,
        description: "Peak hours and weekends",
        is_active: true,
        contact_only: false,
        created_at: subDays(new Date(), 90).toISOString(),
      },
    ],
  },
  {
    id: "demo-room-2",
    studio_id: DEMO_STUDIO.id,
    name: "Studio B",
    description:
      "Medium-sized studio ideal for small groups and private lessons.",
    capacity: 15,
    equipment: {
      floor_type: "marley",
      floor_type_custom: "",
      mirror_type: "partial",
      speaker_connections: ["bluetooth"],
      other_equipment: ["Ballet barres", "Yoga mats"],
    },
    photos: ["https://images.unsplash.com/photo-1535525153412-5a42439a210d"],
    is_active: true,
    dimensions: { width: 25, length: 35, unit: "ft" },
    created_at: subDays(new Date(), 89).toISOString(),
    room_rates: [
      {
        id: "rate-3",
        room_id: "demo-room-2",
        name: "Standard Rate",
        hourly_rate: 35,
        description: "Regular hourly rate",
        is_active: true,
        contact_only: false,
        created_at: subDays(new Date(),89).toISOString(),
      },
    ],
  },
  {
    id: "demo-room-3",
    studio_id: DEMO_STUDIO.id,
    name: "Studio C",
    description:
      "Intimate space perfect for solo practice and small rehearsals.",
    capacity: 8,
    equipment: {
      floor_type: "hardwood",
      floor_type_custom: "",
      mirror_type: "portable",
      speaker_connections: ["3.5mm", "lightning"],
      other_equipment: ["Piano"],
    },
    photos: ["https://images.unsplash.com/photo-1518611012118-696072aa579a"],
    is_active: true,
    dimensions: { width: 15, length: 20, unit: "ft" },
    created_at: subDays(new Date(), 88).toISOString(),
    room_rates: [
      {
        id: "rate-4",
        room_id: "demo-room-3",
        name: "Standard Rate",
        hourly_rate: 25,
        description: "Regular hourly rate",
        is_active: true,
        contact_only: false,
        created_at: subDays(new Date(),88).toISOString(),
      },
    ],
  },
];

// Generate demo renters
const DEMO_RENTERS: Profile[] = [
  {
    id: "renter-1",
    email: "sarah.johnson@example.com",
    is_owner: false,
    full_name: "Sarah Johnson",
    created_at: subDays(new Date(), 60).toISOString(),
  },
  {
    id: "renter-2",
    email: "michael.chen@example.com",
    is_owner: false,
    full_name: "Michael Chen",
    created_at: subDays(new Date(), 45).toISOString(),
  },
  {
    id: "renter-3",
    email: "emily.davis@example.com",
    is_owner: false,
    full_name: "Emily Davis",
    created_at: subDays(new Date(), 30).toISOString(),
  },
  {
    id: "renter-4",
    email: "james.wilson@example.com",
    is_owner: false,
    full_name: "James Wilson",
    created_at: subDays(new Date(), 20).toISOString(),
  },
  {
    id: "renter-5",
    email: "lisa.martinez@example.com",
    is_owner: false,
    full_name: "Lisa Martinez",
    created_at: subDays(new Date(), 15).toISOString(),
  },
];

// Booking type variations
const BOOKING_TYPES = [
  { name: "Rehearsal", hourly_rate: 50, description: "Standard rehearsal rate" },
  { name: "Class", hourly_rate: 35, description: "Group class rate" },
  { name: "Commercial", hourly_rate: 150, description: "Commercial filming rate" },
  { name: "Private Lesson", hourly_rate: 75, description: "Private instruction rate" },
  { name: "Workshop", hourly_rate: 60, description: "Workshop rate" },
];

// Generate bookings
export function generateDemoBookings(): BookingWithDetails[] {
  const bookings: any[] = [];
  const today = startOfDay(new Date());

  // Generate past bookings (for analytics)
  for (let daysAgo = 30; daysAgo > 0; daysAgo--) {
    const bookingDate = subDays(today, daysAgo);
    const numberOfBookings = Math.floor(Math.random() * 4) + 1;

    for (let i = 0; i < numberOfBookings; i++) {
      const room = DEMO_ROOMS[Math.floor(Math.random() * DEMO_ROOMS.length)];
      const renter =
        DEMO_RENTERS[Math.floor(Math.random() * DEMO_RENTERS.length)];
      const startHour = Math.floor(Math.random() * 12) + 8; // 8 AM to 8 PM
      const duration = Math.floor(Math.random() * 3) + 1; // 1-3 hours
      const startTime = addHours(bookingDate, startHour);
      const endTime = addHours(startTime, duration);
      
      // Randomly select a booking type
      const bookingType = BOOKING_TYPES[Math.floor(Math.random() * BOOKING_TYPES.length)];
      const totalPrice = bookingType.hourly_rate * duration;
      
      // Determine status - mostly approved, some canceled or denied
      let status = "approved";
      const statusRand = Math.random();
      if (statusRand < 0.1) status = "canceled";
      else if (statusRand < 0.15) status = "denied";

      const booking = {
        id: `booking-past-${daysAgo}-${i}`,
        room_id: room.id,
        renter_id: renter.id,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        status,
        total_price: totalPrice,
        platform_fee: totalPrice * 0.1,
        stripe_fee: totalPrice * 0.029 + 0.3,
        studio_payout_amount: totalPrice * 0.85,
        contact_name: renter.full_name,
        contact_email: renter.email,
        contact_phone: "+1 (555) 123-4567",
        notes: `Demo rental for ${room.name}`,
        is_instant_book: Math.random() > 0.5,
        rules_acknowledged: true,
        rate_id: `rate-${i}`,
        rate_snapshot: {
          name: bookingType.name,
          hourly_rate: bookingType.hourly_rate,
          description: bookingType.description,
        },
        created_at: subDays(startTime, 3).toISOString(),
        room: {
          name: room.name,
          studio: {
            id: DEMO_STUDIO.id,
            name: DEMO_STUDIO.name,
          },
        },
        renter: {
          email: renter.email,
        },
        booking_slots: [
          {
            id: `slot-${daysAgo}-${i}`,
            booking_id: `booking-past-${daysAgo}-${i}`,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            created_at: subDays(startTime, 3).toISOString(),
          },
        ],
      };

      bookings.push(booking);
    }
  }

  // Generate pending bookings
  const pendingBookings = [
    {
      room: DEMO_ROOMS[0],
      renter: DEMO_RENTERS[0],
      startTime: addHours(today, 26), // Tomorrow at 2 PM
      duration: 2,
      bookingType: BOOKING_TYPES[0], // Rehearsal
    },
    {
      room: DEMO_ROOMS[1],
      renter: DEMO_RENTERS[1],
      startTime: addDays(addHours(today, 10), 2), // 2 days from now at 10 AM
      duration: 3,
      bookingType: BOOKING_TYPES[1], // Class
    },
    {
      room: DEMO_ROOMS[2],
      renter: DEMO_RENTERS[2],
      startTime: addDays(addHours(today, 18), 3), // 3 days from now at 6 PM
      duration: 1,
      bookingType: BOOKING_TYPES[2], // Commercial
    },
  ];

  pendingBookings.forEach((data, index) => {
    const endTime = addHours(data.startTime, data.duration);
    const totalPrice = data.bookingType.hourly_rate * data.duration;

    const booking = {
      id: `booking-pending-${index}`,
      room_id: data.room.id,
      renter_id: data.renter.id,
      start_time: data.startTime.toISOString(),
      end_time: endTime.toISOString(),
      status: "pending",
      total_price: totalPrice,
      platform_fee: totalPrice * 0.1,
      stripe_fee: totalPrice * 0.029 + 0.3,
      studio_payout_amount: totalPrice * 0.85,
      contact_name: data.renter.full_name,
      contact_email: data.renter.email,
      contact_phone: "+1 (555) 123-4567",
      notes: `Pending rental request for ${data.room.name}`,
      is_instant_book: false,
      rules_acknowledged: true,
      rate_id: `rate-pending-${index}`,
      rate_snapshot: {
        name: data.bookingType.name,
        hourly_rate: data.bookingType.hourly_rate,
        description: data.bookingType.description,
      },
      created_at: subDays(new Date(), 1).toISOString(),
      room: {
        name: data.room.name,
        studio_id: data.room.studio_id,
        studio: {
          id: DEMO_STUDIO.id,
          name: DEMO_STUDIO.name,
          timezone: DEMO_STUDIO.timezone,
        },
      },
      renter: {
        email: data.renter.email,
      },
      booking_slots: [
        {
          id: `slot-pending-${index}`,
          booking_id: `booking-pending-${index}`,
          start_time: data.startTime.toISOString(),
          end_time: endTime.toISOString(),
          created_at: subDays(new Date(), 1).toISOString(),
        },
      ],
    };

    bookings.push(booking);
  });

  // Generate today's bookings
  const todayBookings = [
    {
      room: DEMO_ROOMS[0],
      renter: DEMO_RENTERS[3],
      startHour: 10,
      duration: 2,
      bookingType: BOOKING_TYPES[0], // Rehearsal
    },
    {
      room: DEMO_ROOMS[1],
      renter: DEMO_RENTERS[4],
      startHour: 14,
      duration: 1,
      bookingType: BOOKING_TYPES[3], // Private Lesson
    },
    {
      room: DEMO_ROOMS[0],
      renter: DEMO_RENTERS[0],
      startHour: 18,
      duration: 3,
      bookingType: BOOKING_TYPES[4], // Workshop
    },
  ];

  todayBookings.forEach((data, index) => {
    const startTime = addHours(today, data.startHour);
    const endTime = addHours(startTime, data.duration);
    const totalPrice = data.bookingType.hourly_rate * data.duration;

    const booking = {
      id: `booking-today-${index}`,
      room_id: data.room.id,
      renter_id: data.renter.id,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      status: "approved",
      total_price: totalPrice,
      platform_fee: totalPrice * 0.1,
      stripe_fee: totalPrice * 0.029 + 0.3,
      studio_payout_amount: totalPrice * 0.85,
      contact_name: data.renter.full_name,
      contact_email: data.renter.email,
      contact_phone: "+1 (555) 123-4567",
      notes: `Today's rental for ${data.room.name}`,
      is_instant_book: true,
      rules_acknowledged: true,
      rate_id: `rate-today-${index}`,
      rate_snapshot: {
        name: data.bookingType.name,
        hourly_rate: data.bookingType.hourly_rate,
        description: data.bookingType.description,
      },
      created_at: subDays(startTime, 2).toISOString(),
      room: {
        name: data.room.name,
        studio_id: data.room.studio_id,
        studio: {
          id: DEMO_STUDIO.id,
          name: DEMO_STUDIO.name,
          timezone: DEMO_STUDIO.timezone,
        },
      },
      renter: {
        email: data.renter.email,
      },
      booking_slots: [
        {
          id: `slot-today-${index}`,
          booking_id: `booking-today-${index}`,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          created_at: subDays(startTime, 2).toISOString(),
        },
      ],
    };

    bookings.push(booking);
  });

  return bookings as BookingWithDetails[];
}

// Generate demo payouts
export function generateDemoPayouts(): PayoutData[] {
  const payouts: PayoutData[] = [];
  const today = startOfDay(new Date());

  // Generate daily payouts for the past 14 days
  for (let daysAgo = 14; daysAgo >= 1; daysAgo--) {
    const payoutDate = subDays(today, daysAgo);
    const dayOfWeek = payoutDate.getDay();

    // Skip some days randomly to simulate days with no bookings (but less likely on weekends)
    const skipChance = dayOfWeek === 0 || dayOfWeek === 6 ? 0.1 : 0.2;
    if (Math.random() < skipChance && daysAgo > 2) {
      continue;
    }

    // Weekends typically have more bookings
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const baseBookings = isWeekend ? 4 : 2;
    const bookingsCount = baseBookings + Math.floor(Math.random() * 3);

    // Calculate amount based on bookings
    const baseAmount = isWeekend ? 200 : 100;
    const variance = Math.random() * 100 - 50; // +/- 50 variance
    const amount = Math.max(50, baseAmount + variance);

    payouts.push({
      id: `payout-${daysAgo}`,
      studio_id: DEMO_STUDIO.id,
      amount: Math.round(amount * 100) / 100,
      status: daysAgo === 1 ? "processing" : "completed",
      created_at: addDays(payoutDate, 1).toISOString(), // Payout created next day
      period_start: payoutDate.toISOString(),
      period_end: addDays(payoutDate, 1).toISOString(),
      bookings_count: bookingsCount,
      stripe_payout_id: daysAgo === 1 ? undefined : `po_demo_${daysAgo}`,
      stripe_arrival_date:
        daysAgo === 1 ? undefined : addDays(payoutDate, 2).toISOString(),
    });
  }

  return payouts.reverse(); // Return in chronological order (oldest first)
}